import { useTranslation } from 'react-i18next';

import { Check } from 'lucide-react';

import { CloseDiagnosisButton } from './CloseDiagnosisButton';
import { PitchBarTrack } from './PitchBarTrack';
import { PitchGaugeTrack } from './PitchGaugeTrack';
import { PitchTraceGraph } from './PitchTraceGraph';
import {
  MusicNoteIcon,
  PauseButtonIcon,
  PlayButtonIcon,
} from '../../../components';
import { PCM_KEEP_SEC } from '../capture';
import { listeningState } from '../listeningState';
import { SEGMENT_PROMPTS, takeSpanSec } from '../recordTests';

import type { LiveSample, RangeSegmentId, SegmentId } from '../capture';
import type { MelodyNote } from '../melody';
import type { RecordTest } from '../recordTests';

/**
 * RecordTestView — 측정 화면(시안 `보컬 진단_측정 화면`).
 *
 * 지금 무슨 소리를 내야 하는지는 **세그먼트**가 정한다(저음·고음은 한 진단이지만 지시문이
 * 다르다). 세그먼트 누적과 리포트 생성은 useRecordMachine이 하고 여기는 상태를 안 갖는다.
 *
 * 시각화는 진단이 무엇을 재느냐에 따라 갈리고(`RecordTest.viz`), 그림은 각자 컴포넌트가
 * 갖는다 — 지속음은 `PitchBarTrack`(가로 타임라인 + 기준음), 저음·고음은
 * `PitchGaugeTrack`(세로 게이지 + 음이름), 노래는 `PitchTraceGraph`(시간축 곡선).
 * 여기는 어느 쪽을 부를지와 크롬·배치만 소유한다.
 *
 * **음이름은 재는 진단에서만 보여준다.** 지속음은 기준음 대비 벗어남이 값이라 절대
 * 음이름이 판단에 안 쓰이고, 음역 진단은 "어느 음까지 갔나"가 곧 측정값이라 반드시
 * 보여야 한다(시안: 좌측 음정 표시).
 *
 * 화면이 가장자리까지 차야 하므로 `StepShell`의 여백 밖에서 그린다(`bare`). 좌우 여백이
 * 필요한 것은 크롬·안내 카드·버튼뿐이라 그것들만 개별로 갖는다.
 */

interface RecordTestViewProps {
  test: RecordTest;
  segment: SegmentId;
  live: LiveSample;
  /** 벽시계 경과(초) — 상단 타이머와 제한시간 표시가 같은 값을 쓴다. */
  elapsedSec: number;
  isRecording: boolean;
  /** 몇 번째 진단인가(0-기준) — 헤더의 `n/N`. */
  stepIndex: number;
  /** 전체 진단 수. `RECORD_TESTS.length`를 화면이 다시 세지 않게 받아 온다. */
  stepTotal: number;
  onFinish: () => void;
  /** 측정이 멈춰 있나(시안 §5.1 `일시 중단`). */
  paused: boolean;
  onPause: () => void;
  onResume: () => void;
  /** 노래 진단의 정답 멜로디 — 있으면 곡선 아래에 회색으로 깔린다. */
  guide?: MelodyNote[];
  /** 노래 음원의 길이(ms) — 축과 제한시간이 음원을 끝까지 덮게 한다. */
  guideAudioDurationMs?: number;
}

const clock = (sec: number): string => {
  const whole = Math.max(0, Math.floor(sec));
  const mm = String(Math.floor(whole / 60)).padStart(2, '0');
  const ss = String(whole % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

/**
 * 곡선 진단에 제한시간이 빠진 건 설정 오류이지 화면이 다룰 상태가 아니다
 * (`recordTests.spec`이 그 짝을 잡고 있다). 그래도 축 없이 그릴 수는 없으니 다른 곡선
 * 진단과 같은 폭으로 그린다 — 여기서 화면을 비우면 설정 실수가 "소리를 못 들었다"로 보인다.
 */
const TRACE_SPAN_FALLBACK_SEC = 20;

/**
 * 소리를 못 들은 채로도 완료를 열어 주는 시각(초).
 *
 * 시안은 발성이 감지되어야 완료가 활성화된다고 말한다. 그 규칙만 두면 지속음 진단이
 * 막다른 길이 된다 — 제한시간이 없어 기계가 안 끊고, 마이크가 조용하면(권한은 났지만
 * 입력이 없는 기기, 브라우저 mock 경로) 나갈 문이 X뿐이다. X는 진단을 통째로 벗어나므로
 * "이번 시도만 끝내기"가 사라진다.
 *
 * 이건 **탈출구지 "감지됨"이 아니다** — 그래서 이렇게 열린 경우에는 아직 못 들었다고
 * 같이 말한다. 검게 변한 버튼만 두면 소리가 잡혔다는 뜻으로 읽힌다.
 */
const FINISH_FALLBACK_SEC = 5;

/**
 * 듣는 중 표시 — 시안 `1026:47491`의 막대 9개.
 *
 * **진폭계가 아니다.** 캡처가 화면에 주는 값은 프레임의 음높이뿐이라 여기 그릴 레벨이
 * 없다. 시안에서도 이 자리는 정지 그래픽이고, 말하는 바는 "지금 듣고 있다"다 — 그래서
 * 유성일 때만 움직이고 아니면 잦아든다. 레벨을 그리는 척하면 조용히 거짓말이 된다.
 */
const WAVEFORM_BARS = [6, 12, 18, 12, 6, 10, 16, 10, 6];

const ListeningWaveform = ({ active }: { active: boolean }) => {
  const { t } = useTranslation();
  return (
    <div
      role="img"
      aria-label={t('web.diagnosis.record.waveformLabel')}
      className="flex items-center gap-1.5"
    >
      {WAVEFORM_BARS.map((h, i) => (
        <span
          key={`${i}-${h}`}
          aria-hidden="true"
          className={`w-[3px] rounded-[3px] bg-primary transition-opacity ${
            active ? 'opacity-100 motion-safe:animate-pulse' : 'opacity-30'
          }`}
          // 막대마다 위상을 어긋나게 둔다 — 같이 뛰면 파형이 아니라 깜빡임이 된다.
          style={{ height: h, animationDelay: `${i * 90}ms` }}
        />
      ))}
    </div>
  );
};

/**
 * 진행 **숫자**. 막대가 아니라 텍스트인 이유가 둘이다.
 *
 * ① 두 종류의 의미가 정반대다: `gaugeSec`은 발성이 **쌓이는** 눈금(다 차도 안 끊는다),
 *    `limitSec`은 **줄어드는** 마감(0이면 기계가 끊는다). 트랙의 자라는 막대 하나로
 *    둘 다 말하면 저음 진단에서 "오래 버티기를 잘했다"로 읽힌다 — 재는 건 얼마나 낮은
 *    음이냐지 얼마나 오래냐가 아니다.
 * ② 막대는 스크린리더에 아무것도 남기지 않는다.
 */
const ProgressText = ({
  test,
  live,
  elapsedSec,
  limitSec,
}: Pick<RecordTestViewProps, 'test' | 'live' | 'elapsedSec'> & {
  /** 기계가 실제로 끊는 시각(초) — `test.limitSec`이 아니라 `takeSpanSec`의 값이다. */
  limitSec: number | null;
}) => {
  const { t } = useTranslation();
  // **눈금이 먼저다.** 이제 모든 진단에 상한이 있어서 `limitSec`을 먼저 보면 지속음이
  // 쌓인 발성 시간을 영영 말하지 못한다. 상한은 이미 헤더가 목표시간으로 보여주고 있고,
  // 이 줄이 말해야 하는 것은 **이 진단이 재는 값**이다 — 지속음은 얼마나 뻗었나,
  // 나머지는 얼마나 남았나.
  if (test.gaugeSec != null) {
    return (
      <p className="text-sm tabular-nums text-muted-foreground">
        {t('web.diagnosis.record.elapsed', {
          value: live.voicedSec.toFixed(1),
        })}
      </p>
    );
  }
  if (limitSec != null) {
    const left = Math.max(0, limitSec - elapsedSec);
    return (
      <p className="text-sm tabular-nums text-muted-foreground">
        {t('web.diagnosis.record.remaining', { value: Math.ceil(left) })}
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      {t('web.diagnosis.record.recording')}
    </p>
  );
};

/**
 * 멈춤 오버레이 — 시안 `884:9930`. 화면 전체를 덮고 가운데 큰 재생 버튼 하나만 둔다.
 *
 * **오버레이 자체가 버튼이다.** 멈춘 화면에서 할 수 있는 일이 재개 하나뿐인데 작은
 * 과녁을 두면, 뒤 화면이 눌리는 줄 알고 아무 데나 누르는 사람이 아무 반응도 못 얻는다.
 * 뒤 화면은 반투명 흰 막으로 눌러 둔다 — 시안이 배경을 회색조로 죽인다.
 */
const PausedOverlay = ({ onResume }: { onResume: () => void }) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onResume}
      data-testid="record-paused-overlay"
      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-[hsl(var(--surface-lowest))]/80 backdrop-blur-[1px]"
    >
      <PlayButtonIcon
        className="text-[var(--bk-900)]"
        width="5rem"
        height="5rem"
        aria-hidden="true"
      />
      <span className="text-base font-semibold tracking-[-0.5px] text-[var(--bk-900)]">
        {t('web.diagnosis.record.resume')}
      </span>
    </button>
  );
};

export const RecordTestView = ({
  test,
  segment,
  live,
  elapsedSec,
  isRecording,
  stepIndex,
  stepTotal,
  onFinish,
  paused,
  onPause,
  onResume,
  guide,
  guideAudioDurationMs,
}: RecordTestViewProps) => {
  const { t } = useTranslation();
  const prompt = SEGMENT_PROMPTS[segment];
  // 곡선의 가로축·남은 시간·기계의 자동 종료가 **한 값**을 쓴다. 카운트다운이 14초를
  // 말하는데 6초에 끊기면 화면이 거짓말을 한 것이다.
  const limitSec = takeSpanSec(test, guide, guideAudioDurationMs);
  // `voicedSec`은 테이크 안에서 줄지 않는다 — 한 번 들리면 잠깐 쉬어도 버튼이 안 닫힌다.
  const heardVoice = live.voicedSec > 0;
  const canFinish =
    isRecording && (heardVoice || elapsedSec >= FINISH_FALLBACK_SEC);
  // 상단 한 줄 — 소리가 잡히는지만 말한다(음정은 트랙이 말한다).
  const listening = listeningState(live.points, elapsedSec, live.faintAtMs);
  // 노래 진단은 멜로디가 끝나면 기계가 끊는다(`takeSpanSec` → useRecordMachine).
  // 그래서 완료 버튼 자리가 누를 것이 아니라 **상태**가 된다(시안 `1017:17872`).
  const selfEnding = test.viz === 'trace';
  /**
   * **노래 진단은 못 멈춘다.** 채점의 시간 앵커가 레퍼런스 재생 시작 시각이라
   * (`SongReferenceAudio`), 중간에 멈추면 부른 프레임이 엉뚱한 음에 대응된다.
   * 시안도 일시 중단 화면을 기본 발성에만 그린다(`884:9930`).
   */
  const canPause = isRecording && !selfEnding;

  return (
    <div className="relative flex flex-1 flex-col bg-[hsl(var(--surface-lowest))] pb-8">
      {/* 상단 — 시안의 `n/4  <진단명>` + 닫기. 진행 위치를 화면이 늘 말한다.
          **레퍼런스 다시 듣기 버튼을 뺐다**: 녹음 중에는 되감아 틀 수 없고(재생 시작
          시각이 노래 채점의 시간 앵커다 — SongReferenceAudio), 시안의 측정 화면에도
          그 자리가 없다. 늘 잠긴 회색 아이콘은 고장으로 읽힌다. 부르기 전 미리듣기는
          가이드 화면이 준다. */}
      {/* 헤더는 멈춤 오버레이 **위**에 둔다(z-20 > z-10). 멈춘 화면에서 나가는 문까지
          덮으면 재개 말고는 아무것도 못 하는 상태가 된다 — 시안의 멈춤 화면도 헤더를
          지우지 않는다. */}
      <header className="relative z-20 flex items-center px-4 py-2">
        {/* 닫기 버튼과 같은 폭의 자리 — 제목이 화면 진짜 가운데에 놓인다. */}
        <span className="h-11 w-11 shrink-0" aria-hidden="true" />
        <p className="flex-1 text-center text-base font-semibold tracking-[-0.5px]">
          <span className="text-primary tabular-nums">{stepIndex + 1}</span>
          <span className="tabular-nums text-[hsl(var(--border))]">
            /{stepTotal}
          </span>
          <span className="ml-2">{t(test.titleKey)}</span>
        </p>
        <CloseDiagnosisButton />
      </header>

      {/* 경과 시간 — 시안은 큰 숫자 하나와 목표시간을 나란히 둔다. 제한시간이 없는
          진단은 목표 자리를 비운다(회색 `00:00`을 두면 곧 끊길 것처럼 읽힌다). */}
      <div className="mt-4 flex flex-col items-center gap-4">
        <div className="flex flex-col items-center gap-1">
          <p className="text-base text-muted-foreground">
            {t('web.diagnosis.record.elapsedLabel')}
          </p>
          <p className="flex items-baseline gap-2 text-[2rem] font-semibold leading-[1.5] tracking-[-0.5px] tabular-nums">
            <span>{clock(elapsedSec)}</span>
            {limitSec != null && (
              <span className="text-[hsl(var(--muted))]">
                {clock(limitSec)}
              </span>
            )}
          </p>
        </div>
        <ListeningWaveform active={listening === 'hearing'} />
      </div>

      {/* 마이크가 지금 무엇을 듣고 있나 — 진행 시간과 **다른 축**이다.
          노래 진단에는 없다(시안): 화면이 이미 정답 블록과 내 곡선을 나란히 보여주고
          있어서, 그 위에 "잘 듣고 있어요"를 더하면 무엇을 보라는 건지 갈린다. */}
      {!selfEnding && (
        <p
          data-testid="listening-line"
          className={`mt-4 px-8 text-center text-sm ${
            listening === 'silent' ? 'text-primary' : 'text-muted-foreground'
          }`}
        >
          {t(`web.diagnosis.record.listening.${listening}`)}
        </p>
      )}

      {/* 트랙은 가장자리까지 찬다 — 두 그림 다 화면 폭을 다 써야 한다.
          `flex`인 이유: 트랙에 높이를 주는 방법이 백분율이 아니라 교차축 stretch여야
          한다. 셸 루트가 `min-h-dvh`라 조상 높이가 indefinite이고, 거기서 자식의
          `height:100%`는 0이 된다(PitchBarTrack 주석). */}
      <div className="mt-2 flex min-h-0 flex-1">
        {test.viz === 'sustain' && (
          /* 축 눈금은 녹음 상한(30초)이 아니라 **분석 창**(20초)이다. 손잡이는 고정이고
             타임라인이 오른쪽에서 왼쪽으로 흐른다(PitchBarTrack 주석). */
          <PitchBarTrack
            points={live.points}
            startMs={live.takeStartMs ?? 0}
            spanSec={PCM_KEEP_SEC}
          />
        )}
        {test.viz === 'gauge' && (
          <PitchGaugeTrack
            points={live.points}
            segment={segment as RangeSegmentId}
            sung={live.sung}
          />
        )}
        {test.viz === 'trace' && (
          <PitchTraceGraph
            points={live.points}
            sung={live.sung}
            startMs={live.takeStartMs ?? 0}
            spanSec={limitSec ?? TRACE_SPAN_FALLBACK_SEC}
            guide={guide}
          />
        )}
      </div>

      <div className="mt-4 flex flex-col items-center gap-3 px-5">
        {/* 시안의 안내문은 테두리도 배경 틴트도 없는 흰 카드다 — 레드 틴트를 두면
            경고 상태(음성 작음·미감지)와 같은 색이 되어 둘이 구분되지 않는다. */}
        <div className="w-full rounded-3xl bg-[hsl(var(--surface-lowest))] px-4 py-2 text-center">
          <p className="text-sm font-semibold leading-[1.5] tracking-[-0.5px]">
            {t(prompt.titleKey)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t(prompt.subtitleKey)}
          </p>
        </div>

        {/* 탈출구 안내는 진행 숫자를 **대체하지 않고 덧붙는다** — 숫자가 사라지면
            스크린리더에는 이 화면에 아무 상태도 안 남는다. */}
        <div className="flex min-h-[1.25rem] flex-col items-center gap-1">
          {isRecording && (
            <ProgressText
              test={test}
              live={live}
              elapsedSec={elapsedSec}
              limitSec={limitSec}
            />
          )}
          {isRecording && canFinish && !heardVoice && (
            <p className="text-sm text-destructive">
              {t('web.diagnosis.record.noVoiceYet')}
            </p>
          )}
        </div>

        {/* 하단 버튼 둘 — 시안 §5는 `일시 중단`(아웃라인) + `완료`(검정)를 나란히 둔다. */}
        <div className="flex w-full gap-3">
          {canPause && (
            <button
              type="button"
              onClick={onPause}
              className="flex h-[50px] flex-1 items-center justify-center gap-2 rounded-full border-2 border-[hsl(var(--field-border))] text-base font-bold tracking-[0.08px] text-[var(--bk-900)] motion-safe:active:scale-95"
            >
              <PauseButtonIcon className="h-4 w-4" aria-hidden="true" />
              {t('web.diagnosis.record.pause')}
            </button>
          )}
          {selfEnding ? (
            // 누를 수 없는 버튼이 아니라 **상태**로 그린다. 회색으로 잠긴 버튼은 고장으로
            // 읽히고, 여기는 고장이 아니라 "기계가 알아서 끝낸다"는 뜻이다.
            <p
              role="status"
              data-testid="record-status"
              className="flex h-[50px] flex-1 items-center justify-center gap-2 rounded-full bg-primary text-base font-bold text-primary-foreground"
            >
              <MusicNoteIcon className="h-4 w-4" aria-hidden="true" />
              {t('web.diagnosis.record.measuring')}
            </p>
          ) : (
            <button
              type="button"
              onClick={onFinish}
              disabled={!canFinish}
              className="flex h-[50px] flex-1 items-center justify-center gap-2 rounded-full bg-[var(--bk-900)] text-base font-bold tracking-[0.08px] text-white disabled:bg-[var(--bk-300)] motion-safe:active:scale-95"
            >
              <Check className="h-4 w-4" aria-hidden="true" strokeWidth={3} />
              {t('web.diagnosis.record.finishLabel')}
            </button>
          )}
        </div>
      </div>

      {paused && <PausedOverlay onResume={onResume} />}
    </div>
  );
};
